<script>
export default {
    methods: {
        statusFromRatio(ratio) {
            if (ratio < this.okRatio) return 'ok';
            if (ratio < this.koRatio) return 'warning';
            return 'ko';
        },
        // Percentile 90 des ratios : plus fidèle à l'expérience utilisateur que la moyenne
        // (un pic de lenteur peut faire buffer même si la moyenne est bonne).
        percentile90(values) {
            if (!values.length) return 0;

            const sorted = [...values].sort((a, b) => a - b);
            const index = Math.ceil(0.9 * sorted.length) - 1;

            return sorted[Math.max(0, index)];
        },
        // Fait progresser l'anneau degré par degré jusqu'à targetDegrees (0–360).
        async animateRingTo(targetDegrees) {
            const target = Math.max(0, Math.min(360, Math.round(targetDegrees)));

            while (this.ringDegrees < target) {
                if (this.cancelled) break;
                this.ringDegrees += 1;
                await new Promise((resolve) => setTimeout(resolve, 4));
            }

            this.ringDegrees = target;
        },
        async testLevel(level) {
            this.ratios = [];
            this.p90Ratio = 0;
            this.ringDegrees = 0;

            for (let i = 1; i <= this.chunksPerLevel; i++) {
                if (this.cancelled) break;

                const startTime = Date.now();

                try {
                    let chunk = await fetch(`${this.url}/download2/${this.segmentDuration}/${level.id}`, { signal: this.abortController.signal, cache: 'no-store' });

                    if (chunk.status !== 200) {
                        // Segment inaccessible (404, 5xx, etc.) : le palier échoue.
                        return 'unreachable';
                    }

                    await chunk.blob();

                    const duration = (Date.now() - startTime) / 1000; // Temps en secondes
                    const ratio = duration / this.segmentDurationSeconds;

                    this.ratios.push(ratio);
                    this.p90Ratio = this.percentile90(this.ratios);

                    // 10 tests = 360°, 5 tests = 180°, progression degré par degré.
                    await this.animateRingTo((i / this.chunksPerLevel) * 360);
                } catch (e) {
                    if (e.name === 'AbortError') break;
                    // Erreur réseau / fetch : palier en échec.
                    return 'unreachable';
                }

                await new Promise((resolve) =>
                    setTimeout(resolve, 500)
                );
            }

            // Aucun téléchargement réussi (ex. cancel immédiat) → pas de faux positif.
            if (!this.ratios.length) {
                return 'unreachable';
            }

            this.p90Ratio = this.percentile90(this.ratios);
            return this.statusFromRatio(this.p90Ratio);
        },
        async runCascade() {
            this.cancelled = false;
            this.done = false;
            this.levelResults = [];
            this.currentLevelIndex = -1;
            this.ringDegrees = 0;
            this.abortController = new AbortController();

            for (let index = 0; index < this.levels.length; index++) {
                if (this.cancelled) break;

                this.currentLevelIndex = index;
                this.ringDegrees = 0;
                const level = this.levels[index];

                const status = await this.testLevel(level);

                this.levelResults.push({ ...level, status, p90Ratio: this.p90Ratio });

                // On s'arrête uniquement dès qu'un palier passe (ok).
                // Un échec (trop lent ou 404) laisse continuer vers les paliers inférieurs.
                if (status === 'ok' || this.cancelled) break;
            }

            if (!this.cancelled) {
                this.done = true;
                this.$emit('done', { resultKind: this.resultKind });
            }
        },
        ringDashOffset(degrees) {
            return this.ringCircumference * (1 - degrees / 360);
        },
        levelStatus(index) {
            // Si un palier supérieur a passé, tous les paliers inférieurs sont ok.
            const higherOk = this.levelResults.some((level, i) => i < index && level.status === 'ok');
            if (higherOk) return 'ok';

            if (index < this.levelResults.length) {
                const status = this.levelResults[index].status;
                // Affiché comme ko dans TroubleshootingPageStatus.
                return status === 'unreachable' ? 'ko' : status;
            }
            if (index === this.currentLevelIndex) return 'pending';
            return 'unknown';
        },
        // Trait entre deux paliers : coloré si le palier de gauche a un résultat.
        connectorStatus(index) {
            const left = this.levelStatus(index);
            if (left === 'pending' || left === 'unknown') return 'idle';
            return left;
        },
        cancel() {
            this.cancelled = true;
            this.abortController?.abort();
        },
        reset() {
            this.cancel();
            this.done = false;
            this.cancelled = false;
            this.currentLevelIndex = -1;
            this.ringDegrees = 0;
            this.levelResults = [];
            this.ratios = [];
            this.p90Ratio = 0;
        }
    },
    data() {
        return {
            done: false,
            cancelled: false,
            abortController: null,
            currentLevelIndex: -1,
            ringDegrees: 0,
            // Rayon SVG 17 → circonférence pour stroke-dasharray / offset.
            ringCircumference: 2 * Math.PI * 17,
            ratios: [],
            p90Ratio: 0,
            levelResults: [],
        };
    },
    computed: {
        // '1s' / '5s' → secondes numériques pour le ratio temps/durée.
        segmentDurationSeconds() {
            const match = String(this.segmentDuration || '1s').match(/^(\d+(?:\.\d+)?)s?$/);
            return match ? Number(match[1]) : 1;
        },
        currentLevel() {
            return this.currentLevelIndex >= 0 ? this.levels[this.currentLevelIndex] : null;
        },
        // Qualité soutenue : uniquement un palier réellement ok ou warning.
        selectedLevel() {
            const ok = this.levelResults.find(level => level.status === 'ok');
            if (ok) return ok;

            const warning = this.levelResults.find(level => level.status === 'warning');
            if (warning) return warning;

            return null;
        },
        // True si au moins un palier a échoué en HTTP/réseau et qu'aucun palier
        // n'a pu être validé (ok/warning).
        unreachable() {
            if (!this.done || this.selectedLevel) return false;
            return this.levelResults.some(level => level.status === 'unreachable');
        },
        // full_ok | partial_ok | warning_only | full_ko | unreachable | null
        resultKind() {
            if (!this.done) return null;
            if (this.unreachable) return 'unreachable';

            const statuses = this.levelResults.map(level =>
                level.status === 'unreachable' ? 'ko' : level.status
            );
            const hasOk = statuses.includes('ok');
            const hasWarning = statuses.includes('warning');
            const highestOk = this.levelResults.find(level => level.id === this.levels[0].id)?.status === 'ok';

            // Full vert = le 3 Mbps est ok (les paliers inférieurs sont alors verts aussi).
            if (highestOk) return 'full_ok';
            // Au moins un vert (mais pas le max).
            if (hasOk) return 'partial_ok';
            // Aucun vert, au moins un orange.
            if (hasWarning) return 'warning_only';
            return 'full_ko';
        },
        status() {
            if (!this.done) {
                return 'pending';
            }

            switch (this.resultKind) {
                case 'full_ok':
                    return 'ok';
                case 'partial_ok':
                case 'warning_only':
                    return 'warning';
                case 'full_ko':
                case 'unreachable':
                    return 'ko';
                default:
                    return 'unknown';
            }
        }
    },
    props: {
        // Dossier / durée de segment côté serveur : '1s' ou '5s'.
        segmentDuration: {
            default: '1s'
        },
        chunksPerLevel: {
            default: 10
        },
        okRatio: {
            default: 0.5
        },
        koRatio: {
            default: 1
        },
        levels: {
            default: () => ([
                { id: '3000', label: '3 Mbps' },
                { id: '2000', label: '2 Mbps' },
                { id: '1000', label: '1 Mbps' },
                { id: '500', label: '500 Kbps' },
            ])
        },
        url: {},
        enabled: {
            default: false
        },
    },
    translate25Context: 'troubleshooting',
    watch: {
        enabled: function () {
            if (this.enabled) {
                this.runCascade();
            }
        }
    },
    mounted() {
        if (this.enabled) {
            this.runCascade();
        }
    }
};
</script>

<template>
    <div class="troubleshooting-bandwith2">
        <div class="troubleshooting-bandwith2-title-and-value">
            <troubleshooting-page-status :status="status">
                <template #content>
                    <div class="troubleshooting-bandwith2-title">{{ $vt('Reception') }}
                    </div>
                </template>
            </troubleshooting-page-status>
        </div>

        <div class="troubleshooting-bandwith2-timeline">
            <div
                v-for="(level, index) in levels"
                :key="level.id"
                class="troubleshooting-bandwith2-timeline-item"
                :class="{ 'is-last': index === levels.length - 1 }"
            >
                <div
                    class="troubleshooting-bandwith2-timeline-step"
                    :class="[
                        levelStatus(index),
                        { active: index === currentLevelIndex && !done }
                    ]"
                >
                    <div class="troubleshooting-bandwith2-timeline-node-wrap">
                        <svg
                            v-if="levelStatus(index) === 'pending'"
                            class="troubleshooting-bandwith2-timeline-ring"
                            viewBox="0 0 40 40"
                            aria-hidden="true"
                        >
                            <circle
                                class="troubleshooting-bandwith2-timeline-ring-track"
                                cx="20"
                                cy="20"
                                r="17"
                                fill="none"
                                stroke-width="2"
                            />
                            <circle
                                class="troubleshooting-bandwith2-timeline-ring-progress"
                                cx="20"
                                cy="20"
                                r="17"
                                fill="none"
                                stroke-width="2"
                                stroke-linecap="round"
                                :stroke-dasharray="ringCircumference"
                                :stroke-dashoffset="ringDashOffset(ringDegrees)"
                                transform="rotate(-90 20 20)"
                            />
                        </svg>
                        <div class="troubleshooting-bandwith2-timeline-node">
                            <f-icon v-if="levelStatus(index) === 'ok'" icon="fa6-check"></f-icon>
                            <f-icon v-else-if="levelStatus(index) === 'ko'" icon="fa6-xmark"></f-icon>
                            <f-icon v-else-if="levelStatus(index) === 'warning'" icon="fa6-triangle-exclamation"></f-icon>
                            <span v-else class="troubleshooting-bandwith2-timeline-node-empty"></span>
                        </div>
                    </div>
                    <div class="troubleshooting-bandwith2-timeline-label">{{ $vt(level.label) }}</div>
                </div>

                <div
                    v-if="index < levels.length - 1"
                    class="troubleshooting-bandwith2-timeline-connector"
                    :class="connectorStatus(index)"
                ></div>
            </div>
        </div>

    </div>
</template>

<style lang="scss">
.troubleshooting-bandwith2 {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .troubleshooting-bandwith2-title-and-value {
        display: flex;
    }

    .troubleshooting-bandwith2-title {
        display: flex;
        gap: 12px;
        font-family: Lato;
        font-size: 20px;
        font-weight: 700;
    }

    .troubleshooting-bandwith2-timeline {
        display: flex;
        align-items: flex-start;
        gap: 0;
        max-width: 520px;
        width: 100%;
    }

    .troubleshooting-bandwith2-timeline-item {
        display: flex;
        align-items: flex-start;
        flex: 1 1 0;
        min-width: 0;

        &.is-last {
            flex: 0 0 auto;
        }
    }

    .troubleshooting-bandwith2-timeline-step {
        flex: 0 0 auto;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        min-width: 72px;

        .troubleshooting-bandwith2-timeline-node-wrap {
            position: relative;
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .troubleshooting-bandwith2-timeline-ring {
            position: absolute;
            inset: 0;
            width: 40px;
            height: 40px;
            color: var(--black);
            pointer-events: none;
        }

        .troubleshooting-bandwith2-timeline-ring-track {
            stroke: #e6e6e3;
        }

        .troubleshooting-bandwith2-timeline-ring-progress {
            stroke: currentColor;
        }

        .troubleshooting-bandwith2-timeline-node {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            border: 2px solid #d0d0cd;
            background: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            color: #999;
            transition: border-color 0.25s ease, background-color 0.25s ease, color 0.25s ease;
        }

        .troubleshooting-bandwith2-timeline-node-empty {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #d0d0cd;
        }

        .troubleshooting-bandwith2-timeline-label {
            font-family: Lato;
            font-size: 12px;
            font-weight: 700;
            color: #666;
            text-align: center;
            white-space: nowrap;
        }

        &.ok .troubleshooting-bandwith2-timeline-node {
            border-color: var(--primary);
            background: var(--primary);
            color: #fff;
        }

        &.ok .troubleshooting-bandwith2-timeline-label {
            color: var(--primary);
        }

        &.warning .troubleshooting-bandwith2-timeline-node {
            border-color: var(--statusNotice);
            background: var(--statusNotice);
            color: #fff;
        }

        &.warning .troubleshooting-bandwith2-timeline-label {
            color: var(--statusNotice);
        }

        &.ko .troubleshooting-bandwith2-timeline-node {
            border-color: var(--statusNegative);
            background: var(--statusNegative);
            color: #fff;
        }

        &.ko .troubleshooting-bandwith2-timeline-label {
            color: var(--statusNegative);
        }

        &.pending .troubleshooting-bandwith2-timeline-node,
        &.active .troubleshooting-bandwith2-timeline-node {
            border-color: #e6e6e3;
            color: var(--black);
        }

        &.pending .troubleshooting-bandwith2-timeline-label,
        &.active .troubleshooting-bandwith2-timeline-label {
            color: var(--black);
        }
    }

    .troubleshooting-bandwith2-timeline-connector {
        flex: 1 1 auto;
        height: 2px;
        margin-top: 19px;
        background: #e6e6e3;
        min-width: 16px;
        transition: background-color 0.25s ease;

        &.ok {
            background: var(--primary);
        }

        &.warning {
            background: var(--statusNotice);
        }

        &.ko {
            background: var(--statusNegative);
            opacity: 0.55;
        }
    }
}

@media (max-width: 500px) {
    .troubleshooting-bandwith2 {
        .troubleshooting-bandwith2-timeline-step {
            min-width: 56px;

            .troubleshooting-bandwith2-timeline-label {
                font-size: 10px;
            }
        }
    }
}
</style>
